import { useCallback } from 'react';

import { LEADS_PAGE_SIZE } from 'api/constants/leads';
import { useGetLeadsInfiniteQuery } from 'api/leads/getLeads';
import type { ILeadsSortParams, Status } from 'api/types/lead';
import type { IRequestException } from 'api/types/requestException';
import { formatFiltersForAPI } from 'components/Filter/utils/filters';
import type { ITable } from 'components/Table';
import type { Filters } from 'constants/filters';
import type { TFilterState } from 'features/RealEstate/context/FilterContext';

interface IReturnProps {
    canLoadMore: ITable['canLoadMore'];
    isLoadingInitially: ITable['isLoadingInitially'];
    onReachedEnd: ITable['onReachedEnd'];
    tableData: ITable['tableData'];
    refetch: () => void;
    error: IRequestException | null;
}

interface IUseInfiniteTableDataProps {
    status?: Status;
    search?: string;
    pageSize?: number;
    sortParams?: ILeadsSortParams;
    filters?: Record<Filters, TFilterState>;
}

/**
 * A custom hook for fetching the infinite table data on leads pages.
 *
 * @param status - Leads status to fetch.
 * @param search - Lead name to search for.
 */

export const useInfiniteTableData = ({
    status,
    search,
    sortParams,
    pageSize = LEADS_PAGE_SIZE,
    filters,
}: IUseInfiniteTableDataProps): IReturnProps => {
    const filterQueryParams = formatFiltersForAPI(filters);
    const {
        data,
        isFetching,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        error,
        refetch,
    } = useGetLeadsInfiniteQuery({
        status,
        search,
        pageSize,
        sortParams,
        filterQueryParams,
    });

    const onReachedEndHandler = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        isLoadingInitially: isFetching && !isFetchingNextPage,
        canLoadMore: hasNextPage,
        onReachedEnd: onReachedEndHandler,
        tableData: data?.pages.flat(1) ?? [],
        refetch,
        error,
    };
};
